import { useEffect, useRef, useState } from "react";
import { Video, Square, Pause, Play, RotateCcw, Upload, Camera } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Iter-27.62 — Recorder de mini-vidéo selfie (style WhatsApp).
 *
 * Workflow :
 *   1. Click "Activer la caméra" → demande permission webcam
 *   2. Compte à rebours 3-2-1 puis enregistrement (max 60s)
 *   3. Bouton stop → aperçu de la vidéo
 *   4. Bouton "Garder" → upload via /api/upload existant + retourne l'URL au parent
 *   5. Bouton "Refaire" → reset
 *
 * Props :
 *   - onUploaded(url): callback avec l'URL hébergée
 *   - maxSeconds: number (default 60)
 *   - testIdPrefix: string (pour data-testid)
 */
const MAX_DEFAULT_SECONDS = 60;

export default function SelfieRecorder({
    onUploaded,
    maxSeconds = MAX_DEFAULT_SECONDS,
    testIdPrefix = "selfie-recorder",
}) {
    const videoRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const timerRef = useRef(null);
    const streamRef = useRef(null);

    const [state, setState] = useState("idle"); // idle | granted | recording | preview | uploading
    const [elapsed, setElapsed] = useState(0);
    const [blob, setBlob] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    // Cleanup stream on unmount
    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
            }
            if (timerRef.current) clearInterval(timerRef.current);
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const requestCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                // Iter-27.71 — Demande une résolution standard 16:9 (webcam laptop OU smartphone landscape)
                // sans forcer un format portrait : la majorité des webcams renvoient un 16:9 natif et
                // forcer du 9:16 obligeait le navigateur à upscaler/cropper le centre = effet "gros plan facial".
                video: {
                    facingMode: "user",
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                },
                audio: true,
            });
            streamRef.current = stream;
            // Iter-27.69 — On NE peut PAS assigner srcObject ici : le <video> element n'est
            // pas encore monté dans le DOM tant qu'on est en state="idle". On bascule juste
            // le state et un useEffect dédié attache le stream une fois la <video> rendue.
            setState("granted");
        } catch (e) {
            console.error("getUserMedia failed", e);
            toast.error(
                "Impossible d'accéder à la caméra. Vérifie les permissions du navigateur.",
            );
        }
    };

    // Iter-27.69 — Attache le stream au <video> dès qu'il est rendu dans le DOM.
    // Ce hook s'exécute aussi quand on passe de "granted" → "recording" (mais
    // comme c'est le même node React, srcObject reste assigné — bonus de robustesse).
    useEffect(() => {
        const shouldAttach =
            (state === "granted" || state === "recording") &&
            streamRef.current &&
            videoRef.current;
        if (!shouldAttach) return;
        const v = videoRef.current;
        if (v.srcObject !== streamRef.current) {
            v.srcObject = streamRef.current;
        }
        v.muted = true; // évite le larsen
        v.playsInline = true;
        const playPromise = v.play();
        if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch((err) =>
                console.warn("video.play() blocked:", err?.message || err),
            );
        }
    }, [state]);

    const startRecording = () => {
        if (!streamRef.current) return;
        // Choisir le format supporté en priorité webm
        let mimeType = "video/webm;codecs=vp9,opus";
        if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = "video/webm;codecs=vp8,opus";
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = "video/webm";
                if (!MediaRecorder.isTypeSupported(mimeType)) {
                    mimeType = "video/mp4";
                }
            }
        }
        try {
            chunksRef.current = [];
            const mr = new MediaRecorder(streamRef.current, {
                mimeType,
                videoBitsPerSecond: 1_200_000, // ~1.2 Mbps → ~9 MB / minute
            });
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
            };
            mr.onstop = () => {
                const b = new Blob(chunksRef.current, { type: mimeType });
                setBlob(b);
                const url = URL.createObjectURL(b);
                setPreviewUrl(url);
                setState("preview");
                // libère le stream pour économiser la batterie
                if (streamRef.current) {
                    streamRef.current.getTracks().forEach((t) => t.stop());
                    streamRef.current = null;
                }
            };
            recorderRef.current = mr;
            mr.start(250); // chunks tous les 250 ms
            setElapsed(0);
            setState("recording");
            timerRef.current = setInterval(() => {
                setElapsed((prev) => {
                    const next = prev + 1;
                    if (next >= maxSeconds) {
                        stopRecording();
                    }
                    return next;
                });
            }, 1000);
        } catch (e) {
            console.error("MediaRecorder error", e);
            toast.error("Enregistrement vidéo non supporté sur ce navigateur.");
        }
    };

    const stopRecording = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
            recorderRef.current.stop();
        }
    };

    const reset = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setBlob(null);
        setPreviewUrl(null);
        setElapsed(0);
        setState("idle");
    };

    const upload = async () => {
        if (!blob) return;
        setState("uploading");
        try {
            const ext = blob.type.includes("mp4") ? "mp4" : "webm";
            const filename = `selfie-${Date.now()}.${ext}`;
            const fd = new FormData();
            fd.append("file", new File([blob], filename, { type: blob.type }));
            const res = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // L'endpoint retourne {storage_path, file_id, ...}.
            // L'URL servable est /api/files/{storage_path}
            const storagePath = res.data?.storage_path;
            if (!storagePath) throw new Error("Pas de storage_path retourné");
            const apiBase = process.env.REACT_APP_BACKEND_URL || "";
            const url = `${apiBase}/api/files/${storagePath}`;
            toast.success("Vidéo enregistrée");
            onUploaded?.(url);
            reset();
        } catch (e) {
            console.error("upload failed", e);
            toast.error(e?.response?.data?.detail || "Échec de l'upload");
            setState("preview");
        }
    };

    const mmss = (s) => {
        const m = Math.floor(s / 60).toString().padStart(2, "0");
        const sec = (s % 60).toString().padStart(2, "0");
        return `${m}:${sec}`;
    };

    return (
        <div
            className="bg-shariti-black/60 border border-white/10 rounded-3xl overflow-hidden"
            data-testid={testIdPrefix}
        >
            {/* Iter-27.71 — Aspect-video (16:9) cohérent sur tous les écrans + object-contain
                pour ne plus jamais cropper le visage en gros plan. Fond noir entoure si la
                vidéo native n'est pas exactement 16:9 (cas smartphone portrait). */}
            <div className="relative aspect-video bg-black flex items-center justify-center">
                {state === "idle" && (
                    <div className="text-center p-6">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-shariti-orange/15 flex items-center justify-center mb-3">
                            <Camera className="w-8 h-8 text-shariti-orange-light" />
                        </div>
                        <p className="text-sm text-white/65 font-light mb-4 max-w-xs">
                            Enregistre une mini-vidéo selfie de remerciement
                            <br />
                            <span className="text-[11px] text-white/40">
                                (max {maxSeconds}s, sans montage, style WhatsApp)
                            </span>
                        </p>
                        <button
                            onClick={requestCamera}
                            data-testid={`${testIdPrefix}-activate-camera`}
                            className="bg-shariti-orange text-white font-extrabold px-6 py-3 rounded-2xl orange-glow"
                        >
                            Activer la caméra
                        </button>
                    </div>
                )}
                {(state === "granted" || state === "recording") && (
                    <video
                        ref={videoRef}
                        playsInline
                        autoPlay
                        className="w-full h-full object-contain"
                        style={{ transform: "scaleX(-1)" }} // miroir façon selfie
                    />
                )}
                {state === "preview" && previewUrl && (
                    <video
                        src={previewUrl}
                        controls
                        playsInline
                        className="w-full h-full object-contain"
                    />
                )}

                {/* Timer overlay pendant enregistrement */}
                {state === "recording" && (
                    <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                        <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-500 text-white text-xs font-extrabold rounded-full">
                            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                            REC · {mmss(elapsed)}
                        </span>
                        <span className="text-[10px] text-white/55 bg-black/40 px-2 py-1 rounded-full">
                            {mmss(maxSeconds - elapsed)} restant
                        </span>
                    </div>
                )}

                {/* Barre de progression */}
                {state === "recording" && (
                    <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/40">
                        <div
                            className="h-full bg-shariti-orange transition-all duration-1000 linear"
                            style={{ width: `${(elapsed / maxSeconds) * 100}%` }}
                        />
                    </div>
                )}
            </div>

            {/* Contrôles */}
            <div className="px-4 py-3 flex items-center justify-center gap-3">
                {state === "granted" && (
                    <button
                        onClick={startRecording}
                        data-testid={`${testIdPrefix}-start`}
                        className="bg-red-500 hover:bg-red-600 text-white font-extrabold px-6 py-3 rounded-full inline-flex items-center gap-2 transition-all"
                    >
                        <Video className="w-5 h-5" />
                        Démarrer l'enregistrement
                    </button>
                )}
                {state === "recording" && (
                    <button
                        onClick={stopRecording}
                        data-testid={`${testIdPrefix}-stop`}
                        className="bg-white text-shariti-black font-extrabold px-6 py-3 rounded-full inline-flex items-center gap-2"
                    >
                        <Square className="w-5 h-5 fill-current" />
                        Arrêter
                    </button>
                )}
                {state === "preview" && (
                    <>
                        <button
                            onClick={reset}
                            data-testid={`${testIdPrefix}-reset`}
                            className="bg-white/10 hover:bg-white/15 text-white/80 font-bold px-4 py-2.5 rounded-2xl inline-flex items-center gap-2"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Refaire
                        </button>
                        <button
                            onClick={upload}
                            data-testid={`${testIdPrefix}-upload`}
                            className="bg-shariti-yellow text-shariti-black font-extrabold px-5 py-2.5 rounded-2xl inline-flex items-center gap-2"
                        >
                            <Upload className="w-4 h-4" />
                            Garder cette vidéo
                        </button>
                    </>
                )}
                {state === "uploading" && (
                    <p className="text-sm text-white/60">Envoi en cours…</p>
                )}
            </div>
        </div>
    );
}
